import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useActions } from '../../../hooks/useActions';
import { useProcessing } from '../../../hooks/useProcessing';
import { ProductType } from '../../../models/ProductType';
import { ProductSubscriptionRenewPageParamsType } from '../../../models/types/RoutePath';
import { clearOrganizationContracts } from '../../../store/redux-slices/contracts';
import { organizationSelector } from '../../../store/redux-slices/organizations/selectors';
import {
  ORGANIZATION_LOADING_PROCESSING,
  SUBSCRIPTION_RENEWAL_SUGGESTION_LOADING_PROCESSING,
  AA_PRICING_LOADING_PROCESSING,
} from '../../../store/redux-slices/processes/constants';
import { clearAASubscriptionHelperData } from '../../../store/redux-slices/subscriptions/airAmbulance';
import { aaSubscriptionRenewalSuggestionSelector } from '../../../store/redux-slices/subscriptions/airAmbulance/selectors';
import { clearSubscriptionHelperData } from '../../../store/redux-slices/subscriptions/common';
import { loadOrganizationContracts } from '../../../store/saga-slices/contracts';
import { loadOrganization } from '../../../store/saga-slices/organizations';
import { aaLoadStandardPricing } from '../../../store/saga-slices/pricing/airAmbulance';
import {
  loadAASubscriptionRenewalSuggestion,
  loadDatesForNewAASubscription,
} from '../../../store/saga-slices/subscriptions/airAmbulance';
import { ILoadAASubscriptionRenewalSuggestionPayload } from '../../../store/saga-slices/subscriptions/airAmbulance/models';
import { ILoadDatesForNewSubscriptionPayload } from '../../../store/saga-slices/subscriptions/common/models';

export const useSubscriptionRenew = () => {
  const { organizationId, subscriptionId } =
    useParams<ProductSubscriptionRenewPageParamsType>();

  const LoadOrganization = useActions(loadOrganization);
  const LoadOrganizationContracts = useActions(loadOrganizationContracts);
  const ClearOrganizationContracts = useActions(clearOrganizationContracts);
  const LoadStandardPricing = useActions(aaLoadStandardPricing);
  const LoadDatesForNewSubscription = useActions(loadDatesForNewAASubscription);
  const ClearAASubscriptionHelperData = useActions(
    clearAASubscriptionHelperData,
  );
  const ClearSubscriptionHelperData = useActions(clearSubscriptionHelperData);

  const LoadSubscriptionRenewalSuggestion = useActions(
    loadAASubscriptionRenewalSuggestion,
  );

  const subscriptionRenewalSuggestion = useSelector(
    aaSubscriptionRenewalSuggestionSelector,
  );

  const isOrganizationDataLoading = useProcessing(
    ORGANIZATION_LOADING_PROCESSING,
  );

  const isSuggestionDataLoading = useProcessing(
    SUBSCRIPTION_RENEWAL_SUGGESTION_LOADING_PROCESSING,
  );

  const isPricingDataLoading = useProcessing(AA_PRICING_LOADING_PROCESSING);
  const organization = useSelector(organizationSelector);

  const loadSubscriptionRenewalSuggestionHandler = useCallback(() => {
    const payload: ILoadAASubscriptionRenewalSuggestionPayload = {
      organizationId,
      subscriptionId,
    };
    LoadSubscriptionRenewalSuggestion(payload);
  }, [LoadSubscriptionRenewalSuggestion, organizationId, subscriptionId]);

  const loadDatesForNewSubscriptionHandler = useCallback(
    (value: string, forceLoad = false) => {
      if (!value && !forceLoad) {
        return;
      }

      const loadDatesForNewSubscriptionPayload: ILoadDatesForNewSubscriptionPayload =
        {
          organizationId,
          contractId: value,
        };
      LoadDatesForNewSubscription(loadDatesForNewSubscriptionPayload);
    },
    [LoadDatesForNewSubscription, organizationId],
  );

  useEffect(() => {
    LoadOrganizationContracts({ organizationId });
    LoadOrganization(organizationId);

    loadSubscriptionRenewalSuggestionHandler();
    LoadStandardPricing({ productType: ProductType.AirAmbulance });
  }, [
    LoadOrganization,
    LoadOrganizationContracts,
    LoadSubscriptionRenewalSuggestion,
    organizationId,
    subscriptionId,
    LoadStandardPricing,
    loadSubscriptionRenewalSuggestionHandler,
  ]);

  useEffect(
    () => () => {
      ClearAASubscriptionHelperData();
      ClearOrganizationContracts();
      ClearSubscriptionHelperData();
    },
    [
      ClearOrganizationContracts,
      ClearAASubscriptionHelperData,
      ClearSubscriptionHelperData,
    ],
  );

  return {
    isPricingDataLoading,
    isOrganizationDataLoading,
    isSuggestionDataLoading,
    organization,
    organizationId,
    subscriptionId,
    loadSubscriptionRenewalSuggestionHandler,
    loadDatesForNewSubscriptionHandler,
    subscriptionRenewalSuggestion,
  };
};
